/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {getMode, getSetting, Link} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, PropsWithChildren} from 'react';
import {ConditionedUpdate, PlatformUpdate} from "./interfaces";
import User from "../models/userModel";
import {SEARCH_VOLUMES_ROUTE} from "./constants";

function getUpdates(onClose: MouseEventHandler): Array<PlatformUpdate> {
    function ClosingLink(props: PropsWithChildren<{route: string}>) {
        return <Link route={props.route} onClick={onClose}>{props.children}</Link>
    }

    return [
        {
            date: new IDate('2025-05-08'),
            title: 'Review Notificaties!',
            updates: [
                <p>Vanaf nu ontvang je een e-mail wanneer je nieuwe reviews op jouw producten hebt ontvangen! Je hebt
                    30 dagen proef gekregen, hierna kun je het zelf inschakelen voor een kleine meerprijs.</p>
            ]
        },
        {
            date: new IDate('2025-05-08'),
            title: 'Review Inzicht - vernieuwd!',
            updates: [
                <p>Bol gaf ons niet de goede data, we hebben nu een andere oplossing gevonden. Productreview data klopt
                    nu wel! Ook hebben we BolmateAI analyse toegevoegd. Hiervoor heb je 30 dagen proef gekregen van
                    ons, hierna kun je het zelf inschakelen voor een kleine meerprijs.</p>
            ]
        },
        {
            date: new IDate('2024-11-29'),
            title: 'Zoekvolumes vernieuwd',
            updates: [
                <p>De <ClosingLink route={SEARCH_VOLUMES_ROUTE}>Zoekvolume pagina</ClosingLink> is volledig vernieuwd.
                    De resultaten zijn inzichtelijker en bieden een beter beeld van het zoekgedrag bij bol. Ook is het
                    nu eenvoudig terug te vinden hoe een zoekterm over een bepaalde periode presteert t.o.v. van de
                    periode daarvoor.
                </p>
            ]
        },
        {
            date: new IDate('2024-07-16'),
            title: 'Factuuraanvragen voor oude orders',
            updates: [
                <p>Het is vanaf nu mogelijk om volledig automatisch ook factuuraanvragen te verwerken voor orders van
                    voor jouw Bolmate account. Het enige wat je hiervoor hoeft te doen is deze optie in te schakelen via
                    de <ClosingLink route='/facturen/aanvragen?tab=instellingen'>Aanvragen instellingen</ClosingLink> en
                    een factuurnummer reeks te kiezen die het systeem voor de oude facturen moet gebruiken.</p>
            ]
        },
        {
            date: new IDate('2024-06-24'),
            title: 'Gebruikers toevoegen',
            updates: [
                <p>Je kunt vanaf nu meerdere gebruikers toevoegen aan jouw verkoopaccount(s). Zo kun je bijvoorbeeld een
                    klantenservice medewerker met beperkte rechten toevoegen, of de boekhouder toegang geven tot de
                    financiële cijfers. Je kunt gebruikers uitnodigen en beheren via de <ClosingLink
                        route='/instellingen/gebruikers'>instellingen</ClosingLink>.</p>
            ]
        },
        {
            date: new IDate('2024-06-11'),
            title: 'Sales combineren',
            updates: [
                <p>Het is nu mogelijk om op het <ClosingLink route='/sales-dashboard'>Sales Dashboard</ClosingLink> de
                    data van meerdere verkoopaccounts te combineren. Zo kun je bijvoorbeeld in één overzicht de verkopen
                    van al jouw verkoopaccounts zien. Je vindt deze optie (als je meerdere verkoopaccounts hebt
                    gekoppeld) boven de statistieken op de <ClosingLink route='/sales-dashboard'>Sales
                        Dashboard</ClosingLink> pagina.</p>
            ]
        },
        {
            date: new IDate('2024-02-29'),
            title: 'Bol zoektrends',
            updates: [
                <p>Via de nieuwe <ClosingLink route='/zoek-trends'>Zoek Trends</ClosingLink> functionaliteit kun je
                    eenvoudig achterhalen hoe vaak er bij Bol gezocht wordt naar bepaale zoekwoorden. Zo kun je
                    bijvoorbeeld bepalen wat het meest optimale moment is om jouw seizoensproduct te verkopen, of kun je
                    onderzoeken of het zin heeft een bepaald product toe te voegen aan jouw assortiment.
                </p>
            ]
        },
        {
            date: new IDate('2024-01-17'),
            title: 'Verbeteringen Sales Dashboard',
            updates: [
                <p>We hebben met behulp van feedback van onze verkooppartners de (winst)berekeningen van
                    het <ClosingLink route='/sales-dashboard'>Sales
                        Dashboard</ClosingLink> bijgeschaafd, zodat deze in sommige situaties nog betrouwbaarder werkt.
                    Daarnaast hebben we de mogelijkheid van het instellen van de productkosten uitgebreid, zo is het nu
                    onder andere mogelijk om overige kosten toe te voegen per product en/of per order.<br/>De
                    feedback wordt erg gewaardeerd!</p>
            ]
        },
        {
            date: new IDate('2023-03-25'),
            title: 'Uploadregels',
            updates: [
                <p>Het is nu mogelijk om regels in te stellen voor het automatisch uploaden van de facturen. Zo kan je
                    bijvoorbeeld instellen dat alleen facturen voor zakelijke klanten automatisch geüpload moeten
                    worden, of juist niet voor klanten uit een ander land. Je vindt deze instellingen terug op de
                    Facturen {'>'} Aanvragen pagina onder het tabje Instellingen.</p>
            ]
        },
        {
            date: new IDate('2023-03-25'),
            title: 'Verkoopaccount verwijderen',
            updates: [
                <p>Via Instellingen {'>'} Algemeen kun je jouw verkoopaccount verwijderen, zonder dat jouw hele Bolmate
                    account verwijderd wordt. Hiermee kan je bijvoorbeeld een verkoopaccount dat niet meer actief is
                    verwijderen en jouw andere verkoopaccounts behouden.</p>
            ]
        },
        {
            date: new IDate('2023-02-01'),
            title: 'Globaal zoeken & BTW importeren',
            updates: [
                {
                    elements: <p>Bij het opzoeken van een order aan de hand van het ordernummer zal het systeem in al je
                        accounts zoeken als in het actieve account de order niet gevonden kan worden. Als de order in
                        één van je andere accounts gevonden is, dan zal dit getoond worden met een informatie icoontje
                        naast het order nummer. Plaats de cursor op het icoontje om te zien uit welk account de order
                        komt!</p>,
                    userCondition: (user: User | null) => (user?.accounts.length ?? 0) > 1,
                },
                <p className='mt-1'>In <ClosingLink route='/instellingen/producten'>Instellingen {'>'} Production
                    (BTW)</ClosingLink> is het nu mogelijk om via een Excel bestand de BTW percentages van meerdere (of
                    alle) producten te uploaden.</p>
            ],
        },
        {
            date: new IDate('2023-01-17'),
            title: 'Optimalisaties & uitbreiding functionaliteiten',
            updates: [
                <p>We hebben de afgelopen tijd hard gewerkt aan een aantal grote optimalisaties en het uitbreiden van
                    een aantal functionaliteiten. De volgende onderdelen van het platform zijn geoptimaliseerd:
                    <ul>
                        <li>De Bol API implementatie is geüpdatet naar versie 8, waarmee we de orders een stuk
                            sneller kunnen verwerken. Je hoeft zelf niets te doen voor deze update.
                        </li>
                        <li>We hebben onze database op verschillende plekken geoptimaliseerd, waardoor het platform
                            sneller zal presteren.
                        </li>
                    </ul>
                    De volgende functionaliteiten zijn uitgebreid: <ul>
                        <li>E-mails: Het is nu ook mogelijk om een e-mail enkel voor bestellingen met een
                            bepaalde verzendwijze (eigen logistiek, LVB of beide) te verzenden.
                        </li>
                        <li>Exportfunctie: Bij het exporteren van de facturen (via de <ClosingLink
                            route='/instellingen/exporteren'>Instellingen</ClosingLink>) kan je nu ook voor het
                            bestandstype CSV (Aangepast) kiesen. Bij dit bestandstype kan je zelf de gegevens selecteren
                            die je per factuur wilt exporteren.
                        </li>
                    </ul>
                </p>
            ]
        },
        {
            date: new IDate('2022-07-26'),
            title: 'E-mail landselectie',
            updates: [
                <p>Bij het instellen van de automatische e-mails op de <ClosingLink
                    route='/emails'>E-mails</ClosingLink> pagina is het nu mogelijk om de mails enkel te verzenden naar
                    klanten uit geselecteerde landen, via de landenselectie. De standaardinstelling is dat de mail voor
                    ieder land verzonden wordt.
                </p>
            ]
        },
        {
            date: new IDate('2022-06-07'),
            title: 'E-mail productselectie',
            updates: [
                <p>We hebben in de <ClosingLink route='/emails'>E-mails</ClosingLink> de popup waarmee
                    producten voor de e-mails geselecteerd kunnen worden vervangen. Met de nieuwe popup is het
                    overzichtelijker en eenvoudiger om de juiste producten te selecteren voor de gewenste e-mail.</p>
            ]
        },
        {
            date: new IDate('2022-04-13'),
            title: 'Beleidspunten Bol',
            updates: [
                <p>De validatie van de e-mails op de <ClosingLink route='/emails'>E-mails</ClosingLink> pagina is
                    uitgebreid om op meer beleidspunten van Bol te controleren. Hierdoor
                    is het mogelijk dat er een waarschuwing wordt getoond die eerder niet getoond werd. In de
                    waarschuwing wordt aangegeven welke beleidspunten van Bol overschreden worden met de ingestelde
                    e-mails, hiermee kan het probleem eenvoudig en snel opgelost worden.
                </p>
            ]
        },
        {
            date: new IDate('2022-03-02'),
            title: 'E-mails voor resterende producten',
            updates: [
                <p>Het is mogelijk geworden om op de E-mails pagina bij mails in te stellen dat ze alleen verzonden
                    worden voor 'resterende' producten. Dit zijn producten waarvoor geen andere mail ingesteld is. Op
                    deze manier kan eenvoudig voorkomen worden dat er meer dan één mail per bestelling verzonden wordt.
                    Je vindt deze optie in de productselectie van de e-mails op de <ClosingLink
                        route='/emails'>E-mails</ClosingLink> pagina.</p>
            ]
        },
        {
            date: new IDate('2021-11-26'),
            title: 'Automatische retouren en creditfacturen',
            updates: [
                <p>Vanaf vandaag worden automatisch retourzendingen geïmporteerd. Op de <ClosingLink
                    route='/retouren'>Retouren</ClosingLink> pagina worden alle retourzendingen getoond. Hier is het
                    onder andere mogelijk om creditfacturen aan te maken en te verzenden. Via de <ClosingLink
                        route='/instellingen/factuur-instellingen'>Instellingen</ClosingLink> is het ook mogelijk om de
                    creditfacturen automatisch aan te maken én optioneel automatisch te verzenden.</p>
            ]
        },
        {
            date: new IDate('2021-11-11'),
            title: 'Verbeterde e-mail template editor',
            updates: [
                <p>We hebben een nieuwe e-mail template editor toegevoegd aan het platform waarmee het mogelijk is om de
                    e-mails volledig naar eigen wens te ontwerpen. Hiermee is onder andere het volgende mogelijk
                    geworden:</p>,
                <ul>
                    <li>Afbeeldingen / logo's kunnen aan de mail toegevoegd worden.</li>
                    <li>De e-mails zijn automatisch volledig geoptimaliseerd voor mail programma's én mobiele apparaten.
                    </li>
                    <li>De layout van de e-mail template is met een "drag & drop" systeem zeer eenvoudig in te delen.
                    </li>
                </ul>,
                <p>Deze nieuwe editor is helaas niet te gebruiken met reeds bestaande e-mail templates. Om gebruik te
                    maken van de nieuwe editor maak je een nieuwe e-mail template aan via de <ClosingLink
                        route='/emails'>E-mails</ClosingLink>.</p>,
                <p className='mt-1'>Daarnaast wordt voor de handmatige verzending nu ook gebruik gemaakt van de e-mail
                    template van de "Factuur mail". Als je geen gebruik maakt van de "Factuur mail", dan gebruikt het
                    systeem een standaard template. Deze template is te zien door tijdelijk een "Factuur mail" toe te
                    voegen op de E-mails pagina en de daarvoor bestemde standaard template te bekijken.</p>
            ]
        },
        {
            date: new IDate('2021-09-06'),
            title: 'BTW opties & Algemene verbeteringen',
            updates: [
                <p>Twee BTW mogelijkheden zijn toegevoegd om meer ondersteuning te kunnen bieden voor afwijkende
                    situaties.</p>,
                <ul>
                    <li>Het is mogelijk om samengestelde BTW in te stellen in de <ClosingLink
                        route='/instellingen/producten'>BTW instellingen</ClosingLink>.
                    </li>
                    <li>Het is mogelijk om de BTW van een reeds bestaande factuur te verleggen op de <ClosingLink
                        route='/facturen'>Facturen</ClosingLink> pagina.
                    </li>
                </ul>,
                <p>Daarnaast is het vanaf nu mogelijk om zelf een naam voor het account in te stellen indien je gebruik
                    maakt van meerdere accounts. Op deze manier kun je zelf de verschillende accounts uit elkaar halen.
                    Deze mogelijkheid vind je terug in de Bedrijfsgegevens van de <ClosingLink route='/instellingen'>Algemene
                        instellingen</ClosingLink>. Let op, deze optie wordt enkel getoond wanneer je gebruik maakt van
                    meerdere accounts.
                </p>
            ]
        },
        {
            date: new IDate('2021-08-24'),
            title: 'Meerdere accounts koppelen',
            updates: [
                <p>
                    Het is nu mogelijk om meerdere Bol accounts aan je Bolmate account te koppelen. Hierdoor heb je
                    nog maar één login nodig om al je Bol shops te beheren! Rechts bovenin de pagina wordt nu een
                    dropdown getoond, waarbij je van account kunt wisselen en nieuwe accounts kunt toevoegen.
                </p>,
                <i>Informatie over het samenvoegen van bestaande accounts volgt binnenkort.</i>
            ]
        },
        {
            date: new IDate('2021-08-09'),
            title: 'E-mail statistieken',
            updates: [
                <p>Vanaf nu worden het aantal opens en aantal kliks van de verzonden mails bijgehouden. Per soort mail
                    die verzonden wordt is via de <ClosingLink route='/emails'>E-mails</ClosingLink> pagina in te zien
                    hoe vaak de mail verzonden, geopend en geklikt is. Deze statistieken zijn per dag, week, maand,
                    kwartaal en jaar in te zien.</p>
            ]
        },
        {
            date: new IDate('2021-07-22'),
            title: 'Financieel Dashboard',
            updates: [
                'Het financieel dashboard is toegevoegd aan het platform. Deze pagina is te vinden via Dashboard in het ' +
                'menu. De bestellingen zijn nu terug te vinden via Facturen in het menu. Op deze nieuwe pagina worden de ' +
                'volgende gegevens berekend voor de gekozen periode:',
                <ul>
                    <li>Aantal bestellingen</li>
                    <li>Bruto omzet</li>
                    <li>Aantal (geaccepteerde) retourzendingen. Deze worden automatisch geïmporteerd.</li>
                    <li>De top 5 best verkopende producten</li>
                    <li>Vergelijking met de vorige periode</li>
                </ul>
            ]
        },
        {
            date: new IDate('2021-06-25'),
            title: 'Bol API upgrade',
            updates: [
                'De API koppeling met Bol is bijgewerkt naar versie 5. Hierdoor is onder andere het volgende mogelijk geworden:',
                <ul>
                    <li>Snellere verwerking van bestellingen</li>
                    <li>Bestellingen importeren voordat deze verzonden zijn</li>
                    <li>Mails sturen voor geplaatste bestellingen</li>
                </ul>
            ],
        }
    ];
}

export function getUpdatesFrom(user: User | null, onClose: MouseEventHandler) {
    if (user?.isDemoAccount) return [];
    const mode = getMode();
    const devSetting = getSetting('showDevUpdates');
    if (mode === 'dev' && devSetting === 'none') return [];

    const lastNotified = user?.getFlex('lastNotifiedUpdate');
    let notifyFromDate: IDate | null = new IDate(lastNotified);
    if (mode === 'dev' && devSetting === 'all') notifyFromDate = null;

    return getUpdates(onClose).filter((update: PlatformUpdate) => {
        if (!(!notifyFromDate || update.date.isAfter(notifyFromDate)) && (update.userCondition?.(user) ?? true))
            return false;
        const matchingUpdates = update.updates.filter((update: ConditionedUpdate | React.ReactNode) => {
            if (!update) return false;
            if (React.isValidElement(update)) return true;
            return (update as ConditionedUpdate).userCondition?.(user) !== false;
        });
        return !!matchingUpdates.length;
    });
}